import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

/**
 * Configuração de cap de compras da empresa ativa. Lê uma vez no mount
 * e expõe `update` para gravar (PATCH em `companies`). Sem cache global
 * — quem precisar reagir a outras telas que mudaram o valor faz seu próprio
 * fetch (a edição é rara, não justifica realtime).
 */
export interface CompanyBudget {
  /** Teto mensal de compras em R$. 0 = sem teto (feature desligada). */
  purchase_budget_monthly: number
  /** Dia do mês a partir do qual a Sugestão pré-marca Standby. 1..28. */
  purchase_cutoff_day: number
  /** Meta de custo de mercadoria (%) — âncora do teto sugerido (fallback dos meses sem meta própria). */
  cost_target_pct: number
}

const DEFAULT_BUDGET: CompanyBudget = {
  purchase_budget_monthly: 0,
  purchase_cutoff_day: 25,
  cost_target_pct: 55,
}

export function useCompanyBudget() {
  const [budget, setBudget] = useState<CompanyBudget>(DEFAULT_BUDGET)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { if (!cancelled) setLoading(false); return }
      const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
      if (!profile?.company_id) { if (!cancelled) setLoading(false); return }
      const { data, error: err } = await supabase
        .from('companies')
        .select('purchase_budget_monthly, purchase_cutoff_day, cost_target_pct')
        .eq('id', profile.company_id)
        .single()
      if (cancelled) return
      if (err) { setError(err.message); setLoading(false); return }
      setBudget({
        purchase_budget_monthly: Number(data?.purchase_budget_monthly ?? 0),
        purchase_cutoff_day: Number(data?.purchase_cutoff_day ?? 25),
        cost_target_pct: Number(data?.cost_target_pct ?? 55),
      })
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [])

  const update = useCallback(async (patch: Partial<CompanyBudget>): Promise<boolean> => {
    setSaving(true)
    setError(null)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setSaving(false); return false }
    const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
    if (!profile?.company_id) { setSaving(false); return false }
    const next: CompanyBudget = { ...budget, ...patch }
    const { error: err } = await supabase
      .from('companies')
      .update(next)
      .eq('id', profile.company_id)
    setSaving(false)
    if (err) { setError(err.message); return false }
    setBudget(next)
    return true
  }, [budget])

  return { budget, loading, error, saving, update }
}
